import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { MediaFolder } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';

import { useCreateMediaFolder, useRenameMediaFolder } from '../hooks/use-media-folder-mutations';

export const MAX_FOLDER_NAME_LENGTH = 80;

export interface FolderNameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Set to rename; absent to create. */
  folder?: MediaFolder | null;
  onSaved?: (folder: MediaFolder) => void;
}

/** One text field: the folder's name, unique per organization. */
export function FolderNameDialog({ open, onOpenChange, folder, onSaved }: FolderNameDialogProps) {
  const { t } = useTranslation(['media', 'common']);
  const create = useCreateMediaFolder();
  const rename = useRenameMediaFolder();
  const message = useErrorMessage();
  const [name, setName] = useState(folder?.name ?? '');
  const mutation = folder ? rename : create;
  const error = mutation.error;
  const taken = isApiError(error) && error.code === 'ALREADY_EXISTS';
  const trimmed = name.trim();

  const close = (next: boolean) => {
    if (!next) {
      create.reset();
      rename.reset();
    }
    onOpenChange(next);
  };

  const submit = () => {
    if (trimmed === '' || mutation.isPending) return;
    const done = {
      onSuccess: (saved: MediaFolder) => {
        onSaved?.(saved);
        close(false);
      },
    };
    if (folder) rename.mutate({ id: folder.id, name: trimmed }, done);
    else create.mutate(trimmed, done);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md">
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {folder ? t('media:folders.renameTitle') : t('media:folders.createTitle')}
            </DialogTitle>
            <DialogDescription>{t('media:folders.nameDescription')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="media-folder-name">{t('media:folders.nameLabel')}</Label>
            <Input
              id="media-folder-name"
              value={name}
              autoFocus
              maxLength={MAX_FOLDER_NAME_LENGTH}
              placeholder={t('media:folders.namePlaceholder')}
              onChange={(event) => setName(event.target.value)}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'media-folder-name-error' : undefined}
            />
            {error ? (
              <p id="media-folder-name-error" className="text-destructive text-sm" role="alert">
                {taken ? t('media:folders.nameTaken') : message(error)}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => close(false)}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" disabled={trimmed === '' || mutation.isPending}>
              {folder ? t('common:actions.save') : t('media:folders.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
